import type { MapBox } from './map.js';

/**
 * Avenue building frontages. The windowed facade is generated as thin
 * `facade-N` boxes in the map's box list (so the editor can carve them like
 * anything else), while decor.ts adds the non-occluding accents (balconies,
 * cornices) from the same FRONTAGES data. Facade boxes are VISUAL only —
 * buildStatics skips them, so collision comes from the building mass behind.
 */

interface Span {
  x0: number;
  x1: number;
  top: number; // building height over this x-range
}

export interface Frontage {
  z: number; // facade plane
  face: 1 | -1; // direction the facade looks (toward the avenue)
  doors: [number, number][]; // x-ranges left open (galería entrances, roof stairs)
  spans: Span[]; // per-x building heights
}

export const FRONTAGES: Frontage[] = [
  {
    z: 14,
    face: -1,
    doors: [[-17.5, -12.5], [35, 40]],
    spans: [
      { x0: -64, x1: 40, top: 12 },
      { x0: 40, x1: 62, top: 5.7 }, // under the NE roof — open air above
      { x0: 62, x1: 64, top: 12 },
    ],
  },
  {
    z: -14,
    face: 1,
    // [-51,-39] keeps the exterior roof stair clear of facade panels.
    doors: [[-51, -39], [-32.5, -27.5], [7.5, 12.5]],
    spans: [
      { x0: -64, x1: -40, top: 5.7 }, // under the SW roof — open air above
      { x0: -40, x1: 64, top: 12 },
    ],
  },
];

const FACADE_VARIANT_COUNT = 6; // must match the client's FACADE_VARIANTS

/** Split [x0,x1] into segments, removing the door openings (+margin). */
export function segments(x0: number, x1: number, doors: [number, number][]): [number, number][] {
  const cuts = [...doors].sort((a, b) => a[0] - b[0]);
  const out: [number, number][] = [];
  let x = x0;
  for (const [a, b] of cuts) {
    if (a - 0.6 > x) out.push([x, a - 0.6]);
    x = Math.max(x, b + 0.6);
  }
  if (x1 > x) out.push([x, x1]);
  return out;
}

/** Thin windowed facade panels (label `facade-<variant>`), proud of the wall. */
export function facadePanelBoxes(): MapBox[] {
  const out: MapBox[] = [];
  let i = 0;
  for (const f of FRONTAGES) {
    for (const sp of f.spans) {
      for (const [a, b] of segments(sp.x0, sp.x1, f.doors)) {
        const variant = i++ % FACADE_VARIANT_COUNT;
        out.push({
          cx: (a + b) / 2,
          cz: f.z + f.face * 0.05, // stand just proud of the mass (no z-fighting)
          sx: b - a,
          sz: 0.1,
          h: sp.top,
          y0: 0,
          color: [1, 1, 1],
          label: `facade-${variant}`,
        });
      }
    }
  }
  return out;
}
